import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  fileNameLengthValidator,
  fileSizeValidator,
  fileTypeValidator,
  forbiddenCharacters,
  forbiddenCharactersValidator,
} from '@shared/validators';
import {
  REVIEWER_MATERIAL_ALLOWED_FILE_TYPES,
  REVIEWER_MATERIAL_MAX_FILE_SIZE_MB,
} from '@data-access/submissions/constants';

import {
  BannerType,
  WcgBannerComponent,
  WcgButtonComponent,
} from '@wcg/frontend-framework';
import { DocumentType } from '@documents/models';
import { WcgUploadFileComponent } from '@shared/components/upload-file/upload-file.component';
import { UploadReviewerMaterialData } from '@shared/dialogs/types';
import { UploadDialogService } from '../../services/upload-document.service';
import { duplicateFileError, fileProcessingError } from '@shared/constants';
import { WcgUploadReviewerMaterialRowComponent } from '../upload-reviewer-material-row/upload-reviewer-material-row.component';
import { ReviewerMaterialsService } from '@submissions/v1/services';
import { validateFile } from '@shared/utils/upload-file-validator.util';

@Component({
  selector: 'wcg-upload-reviewer-materials',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    ReactiveFormsModule,
    WcgButtonComponent,
    WcgUploadReviewerMaterialRowComponent,
    WcgUploadFileComponent,
    WcgBannerComponent,
  ],
  providers: [UploadDialogService],
  templateUrl: './upload-reviewer-materials.component.html',
  styleUrl: './upload-reviewer-materials.component.css',
})
export class WcgUploadReviewerMaterialsComponent implements AfterViewInit {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _fb = inject(FormBuilder);
  private readonly _reviewerMaterialService = inject(ReviewerMaterialsService);
  private readonly _dialogRef = inject(
    MatDialogRef<WcgUploadReviewerMaterialsComponent>,
  );
  public readonly data = inject<UploadReviewerMaterialData>(MAT_DIALOG_DATA);
  @ViewChild('titleRef')
  titleRef!: ElementRef<HTMLHeadingElement>;
  @ViewChild('uploadFileComponent')
  uploadFileComponent!: WcgUploadFileComponent;
  documentTypes: DocumentType[] = [];
  multiSelect = true;
  bannerClose = signal(false);
  bannerType = BannerType;
  bannerText = signal<string>(duplicateFileError());
  submissionId = '';

  allowedFileTypes = REVIEWER_MATERIAL_ALLOWED_FILE_TYPES.map(
    (type: string) => `.${type}`,
  ).join(', ');

  maxFileSizeMb = REVIEWER_MATERIAL_MAX_FILE_SIZE_MB;

  forbiddenCharacters = forbiddenCharacters.join(', ');
  uploadedFileForm = this._fb.group({
    files: this._fb.array([], [Validators.minLength(1)]),
  });

  get uploadedFiles(): FormGroup[] {
    return (this.uploadedFileForm.get('files') as FormArray)
      .controls as FormGroup[];
  }

  get files(): FormArray<FormGroup> {
    return this.uploadedFileForm.get('files') as FormArray<FormGroup>;
  }

  constructor() {
    this.documentTypes = this.data.documentTypes || [];
    this.multiSelect = this.data.multiSelect || true;
    this._dialogRef.disableClose = true;
    this.submissionId = this.data.submissionId || '';
    if (this.data.hasDuplicateFile) this.bannerClose.set(true);
  }

  ngAfterViewInit() {
    if (this.titleRef) {
      this.titleRef.nativeElement.focus();
    }
  }

  onDeleteFile(index: number) {
    this.files.removeAt(index);
    this.uploadFileComponent.deleteFile();
    this.bannerClose.set(false);
  }

  handleCancel() {
    this._dialogRef.close();
  }

  handleConfirm() {
    if (this.uploadedFileForm.valid) {
      if (!this.submissionId) return;
      const files = this.uploadedFileForm.value.files as {
        file: File;
        documentType: string;
        documentName: string;
      }[];

      files.forEach((item) => {
        const documentType = this.findDocumentType(
          item.documentType,
          this.documentTypes,
        );

        // Use the new signal-based upload method from the service
        this._reviewerMaterialService.uploadReviewMaterialWithSignal(
          {
            file: item.file,
            documentType: documentType?.name || '',
            onUploadComplete: this.data.onUploadComplete,
          },
          documentType,
          this.submissionId,
          this.bannerClose(),
        );
      });

      // Close dialog after starting uploads
      this._dialogRef.close();
    } else {
      this.uploadedFileForm.markAllAsTouched();
    }
  }

  get singleSelectSituation(): boolean {
    return this.multiSelect ? false : this.uploadedFiles.length > 1;
  }

  handleBannerClose() {
    this.bannerClose.set(false);
  }

  async fileUpload(files: File[]) {
    try {
      const validatedFiles = await this._duplicateFilesValidate(files);

      for (const { file, fileHash } of validatedFiles) {
        const fileFormGroup = this._fb.group({
          file: this._fb.control(file, [
            Validators.required,
            fileNameLengthValidator(256),
            // fileSizeValidator takes GB; the reviewer-material cap is stated
            // in MB by the Collab Hub tab's upload hint (ERM-12599).
            fileSizeValidator(REVIEWER_MATERIAL_MAX_FILE_SIZE_MB / 1024),
            fileTypeValidator(REVIEWER_MATERIAL_ALLOWED_FILE_TYPES),
            forbiddenCharactersValidator(),
          ]),
          documentType: this._fb.control(null, [Validators.required]),
          documentName: this._fb.control(''),
          questionId: this._fb.control(''),
          hash: this._fb.control(fileHash),
        });
        this.files.push(fileFormGroup);
      }

      this._cdr.markForCheck();
    } catch (error) {
      console.error('Error during file upload:', error);
      this.bannerText.set(fileProcessingError());
      this.bannerClose.set(true);
    }
  }

  private async _duplicateFilesValidate(
    files: File[],
  ): Promise<{ file: File; fileHash: string }[]> {
    const validatedFiles: { file: File; fileHash: string }[] = [];
    const existingFileNames = new Set(
      this.files.controls.map((control) => control.get('file')?.value.name),
    );
    const existingFileHashes = new Set(
      this.files.controls.map((control) => control.get('hash')?.value),
    );

    for (const file of files) {
      if (existingFileNames.has(file.name)) {
        this.bannerClose.set(true);
        continue;
      }

      const validation = validateFile(file, {
        maxSizeInGB: REVIEWER_MATERIAL_MAX_FILE_SIZE_MB / 1024,
        allowedTypes: REVIEWER_MATERIAL_ALLOWED_FILE_TYPES,
      });
      if (validation !== 'valid') {
        existingFileNames.add(file.name);
        validatedFiles.push({ file, fileHash: '' });
        continue;
      }

      const fileHash = await this._generateFileHash(file);
      if (existingFileHashes.has(fileHash)) {
        this.bannerClose.set(true);
        continue;
      }

      existingFileNames.add(file.name);
      existingFileHashes.add(fileHash);
      validatedFiles.push({ file, fileHash });
    }

    return validatedFiles;
  }

  private _generateFileHash(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);

      reader.onload = async () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
        resolve(hashHex);
      };

      reader.onerror = (error) => reject(error);
    });
  }

  // Helper method to find document type
  private findDocumentType(typeName: string, documentTypes: any[]) {
    return documentTypes.find((type) => type.name === typeName);
  }
}
